/**
 * Long-press state machine for the burst main FAB.
 *
 * Co-located with the burst (rather than promoted to `src/hooks/`)
 * because its move-tolerance + short/long-press discrimination is
 * tailored to the burst's mode-toggle semantics.
 *
 * Gesture contract:
 *   - A quick press/release (before the threshold) fires `onShortPressFired`.
 *   - Holding the pointer for `thresholdMs` without moving more than
 *     `moveTolerancePx` fires `onLongPressFired` and swallows the
 *     subsequent release.
 *   - Any pointer cancel/leave/movement beyond tolerance cancels the
 *     gesture without firing anything.
 *
 * The hook returns a set of pointer handlers ready to spread onto a
 * button element.
 */

import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent as ReactPointerEvent } from "react";

/** Options driving the long-press timing and behaviour. */
export interface UseBurstLongPressOptions {

    /** Duration (ms) after which a held pointer becomes a long-press. */
    "thresholdMs": number;

    /**
     * Pointer displacement tolerance (px). Any movement beyond this
     * radius between pointer-down and either end of the gesture cancels
     * it entirely (neither short nor long press fires).
     */
    "moveTolerancePx": number;

    /**
     * Called when the long-press threshold elapses without movement.
     * Typically toggles the expansion mode and opens the burst.
     */
    "onLongPressFired": () => void;

    /**
     * Called when the pointer is released before `thresholdMs` and the
     * gesture has not been cancelled. Typically toggles the burst
     * open/closed state.
     */
    "onShortPressFired": () => void;
}

/** Pointer handlers returned by the hook, ready to spread on a button. */
export interface UseBurstLongPressHandlers {
    "onPointerDown":   ( event: ReactPointerEvent<HTMLButtonElement> ) => void;
    "onPointerMove":   ( event: ReactPointerEvent<HTMLButtonElement> ) => void;
    "onPointerUp":     () => void;
    "onPointerCancel": () => void;
    "onPointerLeave":  () => void;
    "onContextMenu":   ( event: MouseEvent<HTMLButtonElement> ) => void;
}

/** Internal gesture state tracked per press. */
type PressState = "idle" | "armed" | "fired" | "cancelled";

/**
 * React hook implementing the burst long-press state machine.
 *
 * @param options Configuration. See `UseBurstLongPressOptions`.
 * @returns Pointer handlers to spread on the main FAB button.
 */
export const useBurstLongPress = ( options: UseBurstLongPressOptions ): UseBurstLongPressHandlers => {
    const {
        thresholdMs,
        moveTolerancePx,
        onLongPressFired,
        onShortPressFired,
    } = options;

    // Tracked in refs so pointer ticks don't trigger re-renders.
    const pressStateRef = useRef<PressState>( "idle" );
    const longPressTimeoutRef = useRef<number | null>( null );
    const pointerStartRef = useRef<{
        "x": number;
        "y": number;
    } | null>( null );

    /**
     * Clear the pending timer and wipe the pointer start position.
     * Does not touch `pressStateRef` so callers can decide whether the
     * next state is "cancelled", "fired" or "idle".
     */
    const clearTimer = useCallback( (): void => {
        if ( longPressTimeoutRef.current !== null ) {
            window.clearTimeout( longPressTimeoutRef.current );
            longPressTimeoutRef.current = null;
        }
        pointerStartRef.current = null;
    }, [] );

    /**
     * Arm the long-press timer on pointer-down and record the starting
     * pointer coordinates for the move-tolerance check.
     *
     * @param event Captured pointer-down event.
     */
    const handlePointerDown = useCallback( ( event: ReactPointerEvent<HTMLButtonElement> ): void => {
        clearTimer();
        pressStateRef.current = "armed";
        pointerStartRef.current = {
            "x": event.clientX,
            "y": event.clientY,
        };
        longPressTimeoutRef.current = window.setTimeout( () => {
            longPressTimeoutRef.current = null;
            if ( pressStateRef.current !== "armed" ) {
                return;
            }
            pressStateRef.current = "fired";
            onLongPressFired();
        }, thresholdMs );
    }, [ clearTimer, onLongPressFired, thresholdMs ] );

    /**
     * Cancel the gesture as soon as the pointer travels further than the
     * configured tolerance.
     *
     * @param event Captured pointer-move event.
     */
    const handlePointerMove = useCallback( ( event: ReactPointerEvent<HTMLButtonElement> ): void => {
        if ( pressStateRef.current !== "armed" ) {
            return;
        }
        const start = pointerStartRef.current;
        if ( start === null ) {
            return;
        }
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        if ( deltaX * deltaX + deltaY * deltaY > moveTolerancePx * moveTolerancePx ) {
            pressStateRef.current = "cancelled";
            clearTimer();
        }
    }, [ clearTimer, moveTolerancePx ] );

    /**
     * Resolve the press on pointer-up: fire `onShortPressFired` if the
     * gesture was still armed, otherwise do nothing.
     */
    const handlePointerUp = useCallback( (): void => {
        const state = pressStateRef.current;
        clearTimer();
        pressStateRef.current = "idle";
        if ( state === "armed" ) {
            onShortPressFired();
        }
    }, [ clearTimer, onShortPressFired ] );

    /**
     * Abort the gesture without firing anything. Used for cancel/leave
     * events and for unmount cleanup.
     */
    const abortGesture = useCallback( (): void => {
        if ( pressStateRef.current === "armed" ) {
            pressStateRef.current = "cancelled";
        }
        clearTimer();
    }, [ clearTimer ] );

    /**
     * Prevent the native context menu that a browser would surface on a
     * long-press, so the long-press stays our private gesture.
     *
     * @param event Captured context-menu event.
     */
    const handleContextMenu = useCallback( ( event: MouseEvent<HTMLButtonElement> ): void => {
        event.preventDefault();
    }, [] );

    // Safety net: if the component unmounts mid-gesture, drop the timer.
    useEffect( () => {
        return () => {
            if ( longPressTimeoutRef.current !== null ) {
                window.clearTimeout( longPressTimeoutRef.current );
                longPressTimeoutRef.current = null;
            }
        };
    }, [] );

    return {
        "onPointerDown":   handlePointerDown,
        "onPointerMove":   handlePointerMove,
        "onPointerUp":     handlePointerUp,
        "onPointerCancel": abortGesture,
        "onPointerLeave":  abortGesture,
        "onContextMenu":   handleContextMenu,
    };
};
